import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../../services/api';
import { TIMELINE_VISIBILITY_KEY, ALL_CATEGORIES } from './timelineUtils';

/**
 * Fetches activity timeline events (chart-visible only) and exposes
 * the show/hide toggle persisted in localStorage. Used by the chart
 * overlay components on Overview and Analytics dashboards.
 */
export function useTimelineEvents({ chartOnly = true, limit = 500 } = {}) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabledState] = useState(() => {
    try {
      const v = localStorage.getItem(TIMELINE_VISIBILITY_KEY);
      return v === null ? true : v === '1';
    } catch { return true; }
  });
  const [categories, setCategories] = useState(ALL_CATEGORIES);

  const reload = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (chartOnly) params.set('chart_only', 'true');
    params.set('limit', String(limit));
    return adminApi.get(`/admin/timeline?${params.toString()}`)
      .then((r) => setEvents(r.data?.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [chartOnly, limit]);

  useEffect(() => { reload(); }, [reload]);

  const setEnabled = useCallback((v) => {
    setEnabledState(v);
    try { localStorage.setItem(TIMELINE_VISIBILITY_KEY, v ? '1' : '0'); } catch { /* ignore */ }
  }, []);

  const filtered = enabled
    ? events.filter((e) => categories.includes(e.category))
    : [];

  return { events, filtered, loading, enabled, setEnabled, categories, setCategories, reload };
}
